// facetOwnership.js — WHICH ANSWER IS THIS FACET'S NUMBER AN ANSWER TO?
//
// ═══ THE DEFECT THIS EXISTS TO REMOVE (desk-filter-combinations audit, 2026-09-02) ═══
//
// The server NULLS a facet's counts while that facet's own filter is set — deliberately, because
// a self-facet cannot narrow itself. The Leads client therefore keeps the previous non-null map
// so the numbers do not blink out the moment you tick a source. That anti-flicker rule is right
// and this module preserves it.
//
// What was wrong is the KEY. `leadCountKey` carried only archive / mine / since, and explicitly
// left every filter out — so "previous" survived changes that had nothing to do with the facet
// whose number was being reused. Measured on production, backed by `search_exec_log`:
//
//   rent                        →  result 8,470 · Korter facet 2,037
//   rent + Korter               →  result 1,230 · source map is still the rent-only map
//   rent + Korter + Vake        →  result    80 · source facet STILL shows Korter 2,037
//   clean reload to rent + Vake →  result   470 · Korter facet 144
//   rent + Vake, then Korter    →  identical URL, identical result 80 · Korter shows 144
//
// Same final state, same RPC result, two different sidebars: 2,037 versus 144. Six facets behaved
// this way (district, street, type/kind, transaction/intent, source, metro), and Properties did
// NOT — it clears its scoped metro state, which is why the same audit classified Properties metro
// as correct and Leads metro as defective. Two clients, one response, two interpretations.
//
// ═══ THE RULE ═══
//
// A facet's snapshot is keyed by the FULL canonical membership state with only THAT FACET'S OWN
// KEYS REMOVED. Reuse is exact-signature only; anything else renders no number.
//
//   tick a source   → the source signature is unchanged (sources are its own keys)
//                     → previous source counts still shown          ← anti-flicker preserved
//   tick a district → the source signature CHANGES
//                     → source counts are NOT reused; no number     ← the defect, removed
//
// ⚠️ NO NUMBER IS THE CORRECT DEGRADATION, and it is the whole point. `row()` renders a null count
// as a blank, which is honest; the alternative on screen was a confident wrong one. This repo has
// ruled the same way three times — "a count from a different filter set is a lie with digits in
// it" — and every previous fix replaced a cross-question fallback rather than adding a guard.
//
// ⚠️ OWNERSHIP IS NOT SYMMETRIC, and the asymmetries are the load-bearing part:
//   · `streets` owns streetIds but NOT districtCodes. Street counts are computed WITHIN the
//     chosen district, so changing district genuinely changes the street universe and the
//     snapshot must be dropped. Making districts "its own" would rebuild the original bug for
//     one facet.
//   · `districts` owns BOTH districtCodes and districtOwnCodes — 0370 lets a parent survive being
//     clicked, so the two arrays are one question.
//   · `kinds` owns subtypes as well: a subtype is a narrowing of its kind, not an independent
//     axis, and the kind tree's numbers move with it.
//   · Range facets own only their OWN bound. price counts must survive a price edit and must not
//     survive an area edit — which is why they are keyed per facet rather than as one map.
import { DESK_COUNTS_FRESH_MS } from './deskFreshness.js'

/**
 * The filter keys each facet family is a self-facet of. A key listed here does NOT invalidate
 * that family's snapshot; every other committed key does.
 * ⚠️ Keys are `advFilters` names, matching `canonicalFilters()` output — not wire names.
 */
export const FACET_OWN_KEYS = {
  kinds:         ['kinds', 'subtypes'],
  intents:       ['intents'],
  sources:       ['sources'],
  // ✕ halves too (2026-10-22): the tree commits all four arrays as one question.
  districts:     ['districtCodes', 'districtOwnCodes', 'districtCodesNot', 'districtOwnCodesNot'],
  districtsOwn:  ['districtCodes', 'districtOwnCodes', 'districtCodesNot', 'districtOwnCodesNot'],
  // core 3836: a city pick rides the district arrays, so the city tally is a self-facet of the same four
  cities:        ['districtCodes', 'districtOwnCodes', 'districtCodesNot', 'districtOwnCodesNot'],
  streets:       ['streetIds', 'streetNearbyM'],
  metros:        ['metroIds', 'metroNearbyM'],
  streetCoverage:['streetIds', 'streetNearbyM'],
  // Range facets — each owns exactly its own pair.
  price:         ['priceMin', 'priceMax', 'currency'],
  area:          ['areaMin', 'areaMax'],
  landArea:      ['landAreaMin', 'landAreaMax'],
}

/**
 * Signature for one facet family: canonical state minus that family's own keys, plus the axes
 * that make it a DIFFERENT QUESTION rather than a narrower one (lifecycle bucket, whose leads,
 * period). Those three were the whole of the old key and they stay — the 2026-08-20 archive
 * defect they were added for is real and this must not reintroduce it.
 *
 * ⚠️ Sorted keys, JSON output. An object's insertion order is not part of its meaning, and two
 * identical states must produce one string or every comparison is a coin flip.
 */
export function facetSignature(canonFilters, axes, family) {
  const own = new Set(FACET_OWN_KEYS[family] || [])
  const f = canonFilters || {}
  // ⚠️ Desk filters audit 2026-10-28 DF07: since core 3872 `currency` also qualifies the per-m²
  // bounds, which are NOT the price histogram's own question. With a ppm bound set, USD 1,200/m²
  // and GEL 1,200/m² are different cohorts — dropping currency here reused the old histogram.
  if (family === 'price' && (f.ppmMin != null || f.ppmMax != null)) own.delete('currency')
  const kept = {}
  for (const k of Object.keys(f).sort()) if (!own.has(k)) kept[k] = f[k]
  const a = axes || {}
  return JSON.stringify([a.archive ? 'archive' : 'active', a.mine ? 'mine' : 'all', a.since || '', kept])
}

/**
 * Decide one family's value: the fresh one when present, the previous one ONLY when it answers
 * the same question, otherwise null.
 *
 * ⚠️ `fresh` is checked with `!== undefined && !== null` rather than `??` alone so that a
 * legitimately EMPTY map (`{}`) counts as an answer. A facet with nothing to report is a real
 * response — reusing yesterday's numbers over it is the same defect wearing a different shape.
 */
export function resolveFacet(family, fresh, prevEntry, signature) {
  if (fresh !== undefined && fresh !== null) return { value: fresh, sig: signature, fresh: true }
  if (prevEntry && prevEntry.sig === signature) return { value: prevEntry.value, sig: signature, fresh: false }
  return { value: null, sig: signature, fresh: false }
}

/**
 * Per-facet merge for the range histograms. The server withholds ONE facet at a time (the one
 * whose own bound is set), so the object arrives present-but-missing-a-key and a whole-object
 * `??` would never fire — that is why ranges were already merged rather than replaced.
 *
 * ⚠️ THE BUG THIS FIXES is that the old merge kept every previous key regardless of what else
 * changed, so a price histogram survived a district change. Now each range facet carries its own
 * signature and is dropped when ITS question changes.
 */
// ⚠️ Desk filters audit 2026-10-28 DF08: the server sends the plot histogram as `land_area` and the
// plot-size control reads `rangeFacets.land_area`, but its ownership family is `landArea`. Reading
// `fresh.landArea` threw every plot histogram away. Wire name in and out; family name only for
// the signature.
const RANGE_WIRE = { price: 'price', area: 'area', landArea: 'land_area' }
export function resolveRanges(fresh, prevMap, sigFor) {
  const out = {}
  for (const [family, wire] of Object.entries(RANGE_WIRE)) {
    const sig = sigFor(family)
    const f = fresh && Object.prototype.hasOwnProperty.call(fresh, wire) ? fresh[wire] : undefined
    out[wire] = resolveFacet(family, f, prevMap ? prevMap[wire] : null, sig)
  }
  return out
}

/**
 * F-067 (owner, 2026-09-06: "I see count when the facet is free, then select Facebook and refresh — it's gone").
 * The server withholds a facet's counts while that facet's own bound is set (0687's null discipline) and the client
 * keeps the LAST UNCONSTRAINED answer — which lives in memory, so a page refresh with the bound already set has
 * nothing to keep and the row renders without numbers. The answer is durable now: every fresh value is written to
 * the desk cache under its signature (the state MINUS the facet's own keys — the same question whether or not the
 * facet is ticked), and a resolve with no memory entry seeds from that cache. `read`/`write` are injected so this
 * stays pure and testable; the component passes readDeskCache/writeDeskCache.
 */
// 2026-10-22: the durable answer is keyed by the VIEWER (user + pool/scope) and kept with its fetch time.
// It never expired and was shared by everyone on the browser: another user's or a months-old count
// was seeded as this question's answer. Same window as the other counts (DESK_COUNTS_FRESH_MS, 15 min).
// Entries written before this (a bare map, no `at`) are not trusted.
export const FACET_DURABLE_MAX_AGE_MS = DESK_COUNTS_FRESH_MS
export const facetCacheKey = (family, signature, viewer = '') => `lfc:${family}:${viewer}:${signature}`
export function resolveFacetDurable(family, fresh, prevEntry, signature, read, write, { viewer = '', now = Date.now(), maxAgeMs = FACET_DURABLE_MAX_AGE_MS } = {}) {
  const r = resolveFacet(family, fresh, prevEntry, signature)
  const key = facetCacheKey(family, signature, viewer)
  if (r.fresh) { try { write(key, { at: now, v: r.value }) } catch { /* cache is a convenience */ } return r }
  if (r.value != null) return r
  let cached = null
  try { cached = read(key) } catch { cached = null }
  const at = cached && typeof cached === 'object' ? cached.at : null
  if (Number.isFinite(at) && at <= now && now - at < maxAgeMs && cached.v !== undefined && cached.v !== null)
    return { value: cached.v, sig: signature, fresh: false, seeded: true }
  return r
}
